import React from 'react';
import { ArrowLeft, Copy, ExternalLink, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { CheckoutPreview } from '../Checkout/CheckoutPreview';
import { useCheckoutStore } from '../../store/checkoutStore';
import { useProductStore } from '../../store/productStore';

interface Props {
  onBack: () => void;
}

export const PublishCheckout = ({ onBack }: Props) => {
  const navigate = useNavigate();
  const [copied, setCopied] = React.useState(false);
  const [showCouponOption, setShowCouponOption] = React.useState(true);
  const [slug, setSlug] = React.useState('');
  const [isDeploying, setIsDeploying] = React.useState(false);
  const [deployError, setDeployError] = React.useState<string | null>(null);
  const { currentPage, publishPage, updatePageSettings } = useCheckoutStore();
  const getProduct = useProductStore((state) => state.getProduct);
  
  const mainProduct = currentPage?.productId 
    ? getProduct(currentPage.productId)
    : null;

  React.useEffect(() => {
    if (currentPage) {
      setSlug(currentPage.id);
    }
  }, [currentPage]);

  const checkoutUrl = `${window.location.origin}/checkout/${slug}`;

  const settings = {
    heading: currentPage?.displayName || mainProduct?.name || '',
    features: [
      'All features included in basic plan',
      'Easy and flexible business management',
      'Full time customer support',
      '20 TB cloud storage'
    ],
    buttonText: 'Make payment',
    primaryColor: currentPage?.theme?.primaryColor || '#1E293B',
    logo: currentPage?.theme?.logo || '',
    showCouponOption,
  };

  const handlePublish = async () => {
    if (!currentPage) return;

    try {
      setIsDeploying(true);
      setDeployError(null);

      const updatedPage = {
        ...currentPage,
        slug,
        showCouponOption,
        active: true,
      };
      
      publishPage(updatedPage);
      updatePageSettings({ slug, showCouponOption, active: true });

      // After successful local update, navigate to links page
      navigate('/links');
      
    } catch (error) {
      console.error('Publishing error:', error);
      setDeployError(error instanceof Error ? error.message : 'Failed to publish checkout page');
    } finally {
      setIsDeploying(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(checkoutUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const validateSlug = (value: string) => {
    return value.toLowerCase().replace(/[^a-z0-9-]/g, '-');
  };

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-2 gap-8">
        <div>
          <h2 className="text-xl font-semibold mb-6">Preview & Publish</h2>
          
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-medium text-gray-900 mb-4">Page Settings</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Page URL Slug
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={slug}
                      onChange={(e) => setSlug(validateSlug(e.target.value))}
                      className="flex-1 input-field"
                      placeholder="custom-url-slug"
                    />
                  </div>
                  <p className="mt-1 text-sm text-gray-500">
                    Your checkout page will be available at: {checkoutUrl}
                  </p>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-700">
                      Enable Coupon Code Option
                    </label>
                    <p className="text-sm text-gray-500">
                      Allow customers to enter coupon codes during checkout
                    </p>
                  </div>
                  <button
                    onClick={() => setShowCouponOption(!showCouponOption)}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                      showCouponOption ? 'bg-[#1E293B]' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        showCouponOption ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              </div>
            </div>

            <div className="p-6 bg-gray-50 rounded-xl border border-gray-200">
              <h3 className="font-medium text-gray-900 mb-2">Ready to go live?</h3>
              <p className="text-gray-600 text-sm mb-4">
                Publishing will create a live checkout page that you can share with your customers.
              </p>
              
              {deployError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {deployError}
                </div>
              )}
              
              <button
                onClick={handlePublish}
                disabled={isDeploying}
                className="btn w-full flex items-center justify-center gap-2"
              >
                {isDeploying ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    Publishing...
                  </>
                ) : (
                  'Publish Checkout Page'
                )}
              </button>
            </div>
          </div>
        </div>

        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-4">
            Live Preview
          </h3>
          <div className="border rounded-lg overflow-hidden">
            <CheckoutPreview settings={settings} />
          </div>
        </div>
      </div>

      <div className="flex justify-between pt-6 border-t">
        <button
          onClick={onBack}
          className="btn btn-secondary flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
      </div>
    </div>
  );
};